import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';
import { buildFunctionUrl, getSupabasePublishableKey } from '@/lib/selfHostedUrls';

type EmailChangePortal = 'hire' | 'seek';
type EmailChangeClient = Pick<SupabaseClient<Database>, 'auth'>;

type GovernedEmailChangeInput =
  | { portal: 'hire'; newEmail: string; confirmPassword: string }
  | { portal: 'seek'; newEmail: string; seekRequestId: string };

type EmailChangeError = {
  code: 'EMAIL_CHANGE_INVALID' | 'EMAIL_CHANGE_UNAVAILABLE';
  message: string;
};

const EXTERNAL_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

function failure(code: EmailChangeError['code']): { error: EmailChangeError } {
  return {
    error: {
      code,
      message: 'Unable to start the email change. Please try again.',
    },
  };
}

/**
 * Starts the governed two-message GoTrue email-change flow. The current
 * browser session is used only as authentication for the server operation;
 * GoTrue token hashes and their bindings are delivered directly by email.
 */
export async function initiateGovernedEmailChange(
  client: EmailChangeClient,
  input: GovernedEmailChangeInput,
): Promise<{ error: EmailChangeError | null }> {
  const newEmail = input.newEmail.trim().toLowerCase();
  if (
    !EXTERNAL_EMAIL.test(newEmail) ||
    newEmail.length > 320 ||
    newEmail.includes('@auth.fork.internal') ||
    (input.portal === 'hire' && (!input.confirmPassword || input.confirmPassword.length > 1_024)) ||
    (input.portal === 'seek' && !UUID.test(input.seekRequestId))
  ) return failure('EMAIL_CHANGE_INVALID');

  const { data, error } = await client.auth.getSession();
  const accessToken = data.session?.access_token;
  if (error || typeof accessToken !== 'string' || !accessToken) {
    return failure('EMAIL_CHANGE_INVALID');
  }

  try {
    const response = await fetch(buildFunctionUrl('public-auth'), {
      method: 'POST',
      cache: 'no-store',
      credentials: 'omit',
      redirect: 'error',
      referrerPolicy: 'no-referrer',
      headers: {
        'Content-Type': 'application/json',
        apikey: getSupabasePublishableKey(),
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        operation: 'auth_email_change_init',
        portal: input.portal,
        newEmail,
        ...(input.portal === 'hire'
          ? { confirmPassword: input.confirmPassword }
          : { seekRequestId: input.seekRequestId }),
      }),
    });
    const contentType = response.headers.get('content-type')?.split(';', 1)[0]?.trim();
    if (contentType !== 'application/json') return failure('EMAIL_CHANGE_UNAVAILABLE');
    const body = await response.json().catch(() => null) as { ok?: boolean } | null;
    return response.ok && body?.ok === true
      ? { error: null }
      : failure('EMAIL_CHANGE_INVALID');
  } catch {
    return failure('EMAIL_CHANGE_UNAVAILABLE');
  }
}
